import { useState, useEffect, useCallback } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || '';

/**
 * Hook for fetching rework queue data for a work center.
 *
 * Returns rework items that need attention and inline warnings
 * for in-progress items that have pending rework.
 *
 * @param {string} workCenter - PA, OPTI, or ASSEMBLY
 * @param {Object} options - Configuration options
 * @param {number} options.refreshInterval - Auto-refresh interval in ms (default: 60000)
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 */
export function useReworkQueue(workCenter, options = {}) {
  const {
    refreshInterval = 60000,
    autoFetch = true,
  } = options;

  const [queueItems, setQueueItems] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  const fetchQueue = useCallback(async () => {
    if (!workCenter) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch queue items and warnings in parallel
      const [queueRes, warningsRes] = await Promise.all([
        fetch(`${API_BASE}/api/rework-queue/${workCenter.toUpperCase()}`),
        fetch(`${API_BASE}/api/rework-warnings/${workCenter.toUpperCase()}`),
      ]);

      if (!queueRes.ok) {
        throw new Error('Failed to fetch rework queue');
      }
      if (!warningsRes.ok) {
        throw new Error('Failed to fetch rework warnings');
      }

      const queueData = await queueRes.json();
      const warningsData = await warningsRes.json();

      setQueueItems(queueData.items || []);
      setWarnings(warningsData.warnings || []);
      setLastFetch(new Date());
    } catch (err) {
      console.error('[useReworkQueue] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [workCenter]);

  // Initial fetch
  useEffect(() => {
    if (autoFetch && workCenter) {
      fetchQueue();
    }
  }, [autoFetch, workCenter, fetchQueue]);

  // Auto-refresh
  useEffect(() => {
    if (!refreshInterval || !workCenter) return;

    const interval = setInterval(fetchQueue, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval, workCenter, fetchQueue]);

  /**
   * Get warnings for a specific job number.
   * Used to display inline warnings on in-progress items.
   */
  const getWarningsForJob = useCallback((jobNumber) => {
    return warnings.filter(w => w.jobNumber === jobNumber);
  }, [warnings]);

  /**
   * Check if a job has any pending rework.
   */
  const hasRework = useCallback((jobNumber) => {
    return queueItems.some(item => item.jobNumber === jobNumber) ||
           warnings.some(w => w.jobNumber === jobNumber);
  }, [queueItems, warnings]);

  return {
    queueItems,
    warnings,
    loading,
    error,
    lastFetch,
    refetch: fetchQueue,
    getWarningsForJob,
    hasRework,
    // Computed values
    scrapCount: queueItems.filter(i => i.severity === 'scrap').length,
    totalCount: queueItems.length,
  };
}

/**
 * Hook for fetching rework events for a project (by quote number).
 *
 * Used in Project Detail page to show rework history.
 *
 * @param {string} quoteNumber - Quote/project number
 */
export function useProjectRework(quoteNumber) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ openCount: 0, totalHours: 0 });

  const fetchEvents = useCallback(async () => {
    if (!quoteNumber) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `${API_BASE}/api/rework-events?quoteNumber=${encodeURIComponent(quoteNumber)}&includeTimeStats=true`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch rework events');
      }

      const data = await res.json();
      setEvents(data.events || []);

      // Calculate stats
      const open = data.events.filter(e => e.status === 'open').length;
      const totalSeconds = data.events.reduce((sum, e) => sum + (e.totalReworkSeconds || 0), 0);
      setStats({
        openCount: open,
        totalHours: (totalSeconds / 3600).toFixed(1),
      });
    } catch (err) {
      console.error('[useProjectRework] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [quoteNumber]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return {
    events,
    loading,
    error,
    stats,
    refetch: fetchEvents,
  };
}

/**
 * Hook for creating rework events.
 */
export function useCreateReworkEvent() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createEvent = useCallback(async (data) => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/rework-events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to create rework event');
      }

      return await res.json();
    } catch (err) {
      console.error('[useCreateReworkEvent] Error:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createEvent,
    loading,
    error,
  };
}

export default useReworkQueue;
